/*
  Hatch COXO CX207-G product page (sections/hx-cx207g.liquid).
  The scroll engine is the approved prototype's (cx207g/reference/page-source.html), kept
  as is: easing, blending, intro, caption windows, travel keyframes, decode scheduler and
  eviction. Three things differ, all forced by running inside the theme:
  1. Frames load from the CDN as Blobs (the prototype embedded them). The URL pattern and
     counts come from data attributes on the section.
  2. The sticky stages sit below the theme's sticky header, so scroll progress is measured
     against the stage itself (top T, its own height) instead of the whole window. Phone
     browsers change the window height as their toolbars hide and show; the stage's height
     does not, so the animation no longer jumps when that happens.
  3. Quantity, Add to cart and Buy now use the theme's cart: form[data-hx-add] in hatch.js,
     and for Buy now a cart form that returns to checkout.
  Phones (820px and narrower) load every second frame of both sequences and blend between
  them, which halves the download and the decoding, keep fewer decoded frames in memory,
  and follow the finger more closely. Desktop is unchanged.
*/
(() => {
  const started = new WeakSet();

  function boot(root) {
    if (!root || started.has(root)) return;
    started.add(root);
    let alive = true;
    document.addEventListener('shopify:section:unload', (e) => { if (e.target.contains(root)) alive = false; });
    const $ = (sel) => root.querySelector(sel);
    const $$ = (sel) => [...root.querySelectorAll(sel)];

    /* Quantity: every Add to cart and Buy now on the page takes the stepper's quantity */
    (function () {
      let q = 1;
      const out = $('[data-cx-qty-val]');
      function wire() {
        $$('[data-cx-qty-input]').forEach((input) => { input.value = q; });
      }
      const set = (n) => { q = Math.min(99, Math.max(1, n)); if (out) out.textContent = q; wire(); };
      const minus = $('[data-cx-qty-minus]');
      const plus = $('[data-cx-qty-plus]');
      if (minus) minus.addEventListener('click', () => set(q - 1));
      if (plus) plus.addEventListener('click', () => set(q + 1));
      wire();
    })();

    /* Buy now posts to the cart and on to checkout: one submit only, or a double click adds twice */
    root.addEventListener('submit', (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-cx-buy]')) return;
      if (form.dataset.sent) { e.preventDefault(); return; }
      form.dataset.sent = '1';
      form.querySelector('[type="submit"]').setAttribute('aria-busy', 'true');
    });
    // Coming back from checkout restores the page from the back-forward cache: allow Buy now again
    addEventListener('pageshow', () => {
      $$('[data-cx-buy]').forEach((form) => { delete form.dataset.sent; const b = form.querySelector('[type="submit"]'); if (b) b.removeAttribute('aria-busy'); });
    });

    /* LED switch */
    (function () {
      const sec = $('[data-cx-light]');
      const btn = $('[data-cx-switch]');
      const label = $('[data-cx-switch-label]');
      if (!sec || !btn) return;
      btn.addEventListener('click', () => {
        const on = !sec.classList.contains('is-lit');
        sec.classList.toggle('is-lit', on);
        btn.setAttribute('aria-checked', on);
        if (label) label.textContent = on ? btn.dataset.on : btn.dataset.off;
      });
    })();

    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const mobile = () => innerWidth <= 820;
    // Decided once at load, like the first frame the section's inline script fetched
    const small = matchMedia('(max-width: 820px)').matches;
    const STEP = small ? 2 : 1; // load every STEP-th frame
    const R = 1 / STEP; // decode and keep-in-memory distances shrink with it
    const TAU = matchMedia('(pointer: coarse)').matches ? 45 : 95; // scroll smoothing, ms
    const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
    const smooth = (t) => t * t * (3 - 2 * t);
    const canBitmap = typeof createImageBitmap === 'function';
    const conn = navigator.connection;
    const saveData = !!(conn && conn.saveData);

    /* Frame store: compressed frames stay in memory as Blobs, decoded bitmaps only for a
       small window around what is on screen, decoded off the main thread ahead of time. */
    const wanted = [];
    let inflight = 0;
    const MAXJOBS = small ? 2 : 4;
    function pump() {
      if (wanted.length) {
        wanted.sort((a, b) => a.d - b.d);
        for (const w of wanted) { if (inflight >= MAXJOBS) break; w.s.start(w.i); }
        wanted.length = 0;
      }
    }

    /* Network: a few frames at a time, nearest to what is (or is about to be) on screen first */
    const NETJOBS = 6;
    let netInflight = 0;
    const seqs = [];
    // Nothing but the first frame is requested before the page's first paint
    let painted = false;
    function netPump() {
      if (!painted) return;
      while (netInflight < NETJOBS) {
        let best = null;
        for (const s of seqs) {
          const c = s.pick();
          if (c && (!best || c.d < best.d)) best = c;
        }
        if (!best) break;
        best.s.get(best.i);
      }
    }

    // Sequence position i is file frame i * STEP
    function Seq(pattern, total, loop, onFirst) {
      const n = Math.ceil(total / STEP);
      const blobs = new Array(n), bm = new Array(n), pend = new Uint8Array(n), net = new Uint8Array(n), fails = new Uint8Array(n);
      const idx = (i) => (loop ? ((i % n) + n) % n : clamp(i, 0, n - 1));
      const url = (i) => pattern.replace('{i}', String(i * STEP).padStart(3, '0'));
      function load(i) {
        if (bm[i] || pend[i] || !blobs[i]) return;
        pend[i] = 1; inflight++;
        const done = (b) => { bm[i] = b; pend[i] = 0; inflight--; if (onFirst) { const f = onFirst; onFirst = null; f(); } };
        const viaImg = () => {
          const u = URL.createObjectURL(blobs[i]);
          const im = new Image();
          im.onload = () => { URL.revokeObjectURL(u); done(im); };
          im.onerror = () => { URL.revokeObjectURL(u); pend[i] = 0; inflight--; };
          im.src = u;
        };
        if (!canBitmap) return viaImg();
        createImageBitmap(blobs[i]).then(done, viaImg);
      }
      // The CDN answers a plain fetch with a PNG ten times the size: ask for WebP
      function take(i, request) {
        net[i] = 1; netInflight++;
        request
          .then((b) => { if (!b) throw new Error('empty'); blobs[i] = b; }, (e) => { throw e; })
          .catch(() => { net[i] = 0; fails[i]++; })
          .finally(() => { netInflight--; netPump(); }); // keeps loading while the animation loop rests
      }
      function get(i, priority) {
        take(i, fetch(url(i), { headers: { Accept: 'image/webp,*/*' }, priority: priority || 'low' })
          .then((r) => { if (!r.ok) throw new Error(r.status); return r.blob(); }));
      }
      const missing = (i) => !blobs[i] && !net[i] && fails[i] < 3;
      const self = {
        n, idx, url, start: load, get, take,
        on: false, // background loading allowed (otherwise only frames near what is drawn)
        path: null, // frames to fetch first, in order (the intro)
        near: [], // [{c, bias}]: positions to load around, nearest first
        span: n, // how far from those positions to fill in the background
        has: (i) => !!blobs[idx(i)],
        at(i) { return bm[idx(i)] || null; },
        nearest(f) { const c = idx(Math.round(f)); for (let d = 0; d < n; d++) { const a = bm[idx(c + d)]; if (a) return a; const b = bm[idx(c - d)]; if (b) return b; } return null; },
        focus(c, r, bias) {
          const ci = Math.round(c);
          for (let d = 0; d <= r; d++) {
            for (const i of (d ? [idx(ci + d), idx(ci - d)] : [idx(ci)])) {
              if (!bm[i] && !pend[i] && blobs[i]) wanted.push({ s: self, i, d: d + (bias || 0) });
            }
          }
        },
        evict(centers, keep) {
          for (let i = 0; i < n; i++) {
            if (!bm[i]) continue;
            let near = false;
            for (const c of centers) { let d = Math.abs(i - idx(Math.round(c))); if (loop) d = Math.min(d, n - d); if (d <= keep) { near = true; break; } }
            if (!near) { if (bm[i].close) bm[i].close(); bm[i] = null; }
          }
        },
        // The next frame to fetch: the intro path first, then outward from the wanted positions
        pick() {
          if (self.path) {
            for (const i of self.path) if (missing(i)) return { s: self, i, d: -1 };
            self.path = null;
          }
          // Before background loading starts, only what is about to be drawn
          const span = self.on ? self.span : Math.ceil(8 * R);
          let best = null;
          for (const p of self.near) {
            const ci = Math.round(p.c);
            for (let d = 0; d <= span; d++) {
              if (best && d + p.bias >= best.d) break;
              const a = idx(ci + d), b = idx(ci - d);
              if (missing(a)) { best = { s: self, i: a, d: d + p.bias }; break; }
              if (missing(b)) { best = { s: self, i: b, d: d + p.bias }; break; }
            }
          }
          return best;
        },
        ready: () => { for (let i = 0; i < n; i++) if (!blobs[i]) return false; return true; },
      };
      seqs.push(self);
      return self;
    }

    function painter(canvas, seq, blend) {
      const ctx = canvas.getContext('2d');
      let lastA = null, lastB = null, lastT = -1;
      const W = canvas.width, H = canvas.height;
      return (f) => {
        let a, b = null, t = 0;
        if (blend) { const i0 = Math.floor(f); t = Math.round((f - i0) * 8) / 8; a = seq.at(i0); b = t > 0 ? seq.at(i0 + 1) : null; if (!a || (t > 0 && !b)) { a = seq.nearest(f); b = null; t = 0; } }
        else a = seq.at(Math.round(f)) || seq.nearest(f);
        if (!a || (a === lastA && b === lastB && t === lastT)) return;
        lastA = a; lastB = b; lastT = t;
        ctx.globalAlpha = 1; ctx.clearRect(0, 0, W, H); ctx.drawImage(a, 0, 0, W, H);
        if (b) { ctx.globalAlpha = t; ctx.drawImage(b, 0, 0, W, H); ctx.globalAlpha = 1; }
      };
    }

    const spin = $('[data-cx-spin]');
    const macroCanvas = $('[data-cx-macro]');
    const orbitCanvas = $('[data-cx-orbit-canvas]');
    if (!spin || !macroCanvas || !orbitCanvas) return;

    let start = 0;
    let firstAt = 0;
    const body = Seq(root.dataset.body, +root.dataset.bodyCount, true, () => { spin.classList.add('is-ready'); firstAt = performance.now(); });
    const head = Seq(root.dataset.head, +root.dataset.headCount, false);
    const drawSpin = painter(spin, body, true);
    const drawMacro = painter(macroCanvas, head, STEP > 1); // blends over the skipped frames on phones
    const drawOrbit = painter(orbitCanvas, body, true);

    const turn = $('.cx-turn');
    const headSec = $('[data-cx-head]');
    const viewSec = $('.cx-viewer');
    const stage = $('[data-cx-stage]');
    const heroCopy = $('[data-cx-hero]');
    const hint = $('[data-cx-hint]');
    const caps = $$('[data-cx-cap]').map((el) => ({ el, a: +el.dataset.in, b: +el.dataset.out }));
    const ticks = $$('[data-cx-tick]');
    const feats = $$('[data-cx-feat]');
    const glow = $('[data-cx-glow]');

    const KEYS = [[0, 17], [.33, 17], [.44, -17], [.56, -17], [.67, 17], [.78, 17], [.89, -17], [1, -17]];
    function xAt(p) { for (let i = 1; i < KEYS.length; i++) { if (p <= KEYS[i][0]) { const [p0, x0] = KEYS[i - 1], [p1, x1] = KEYS[i]; return x0 + (x1 - x0) * smooth((p - p0) / (p1 - p0 || 1)); } } return KEYS[KEYS.length - 1][1]; }

    const INTRO = 2000, QUARTER = Math.round(body.n / 4);
    let prev = performance.now(), curP = -1, curH = -1, lastActive = -2, lastFeat = -1, lastScrim = -1, tick = 0;
    let of = body.n * 2 / 3, vel = 0, dragging = false, lx = 0;
    const ease = (dt, tau) => (reduce ? 1 : 1 - Math.exp(-dt / tau));
    const seen = (r) => r.bottom > -80 && r.top < innerHeight + 80;
    // Stages stick at T (below the theme header). Progress runs from the moment a stage sticks
    // to the moment it lets go, which depends on its own height (SH, HH), not the window's.
    const headStage = $('.cx-head__stage');
    let T = 0, SH = 1, HH = 1;
    const measure = () => {
      T = parseFloat(getComputedStyle(stage).top) || 0;
      SH = stage.offsetHeight || innerHeight - T;
      HH = (headStage && headStage.offsetHeight) || SH;
    };
    measure();
    addEventListener('resize', measure, { passive: true });
    const prog = (r, h) => clamp((T - r.top) / (r.height - h), 0, 1);
    // Style writes only when the value changes: an unchanged write still costs a style pass
    const put = (el, prop, val) => { const k = '_cx_' + prop; if (el[k] !== val) { el[k] = val; el.style[prop] = val; } };

    /* Loading order: the intro's first frame (already requested by the section); after the
       first paint, the rest of the quarter turn the intro plays and the frames around what is
       on screen; once the visitor scrolls or interacts, the other body frames in the
       background. Head frames start when the close-up is near. */
    const introStart = reduce ? 0 : body.idx(-QUARTER);
    const early = window.hxCx207gFirst; // started by the section's inline script (a file frame number)
    if (early && early.i === introStart * STEP) body.take(introStart, early.blob);
    else body.get(introStart, 'high');
    if (!reduce) {
      body.path = [];
      for (let i = Math.round(-QUARTER); i <= 0; i++) body.path.push(body.idx(i));
    }
    body.near = [{ c: introStart, bias: 0 }];
    const afterPaint = () => { if (painted) return; painted = true; netPump(); };
    const paintTypes = (window.PerformanceObserver && PerformanceObserver.supportedEntryTypes) || [];
    if (paintTypes.includes('paint')) {
      const po = new PerformanceObserver((list) => {
        if (list.getEntries().some((e) => e.name === 'first-contentful-paint')) { po.disconnect(); afterPaint(); }
      });
      po.observe({ type: 'paint', buffered: true });
    } else {
      requestAnimationFrame(() => setTimeout(afterPaint, 0)); // after the first rendered frame
    }
    setTimeout(afterPaint, 3000); // a page opened in a background tab never paints
    const goBackground = () => { if (body.on) return; body.on = true; netPump(); };
    ['scroll', 'wheel', 'touchstart', 'pointerdown', 'keydown'].forEach((type) => addEventListener(type, goBackground, { once: true, passive: true }));
    if (saveData) { body.span = Math.ceil(24 * R); head.span = Math.ceil(24 * R); }

    /* The loop rests while none of the three animated sections is on screen (the same 80px
       margin as seen()), and wakes when one comes back. Head frames start loading when the
       close-up is within two and a half screens, even while the loop rests. */
    let running = true;
    const inView = new Set();
    let viewKnown = !('IntersectionObserver' in window);
    const wake = () => { if (running || !alive) return; running = true; prev = performance.now(); requestAnimationFrame(frame); };
    if (!viewKnown) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => (e.isIntersecting ? inView.add(e.target) : inView.delete(e.target)));
        viewKnown = true;
        if (inView.size) wake();
      }, { rootMargin: '80px 0px' });
      [turn, headSec, viewSec].forEach((el) => io.observe(el));
      const approach = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        head.on = true;
        if (!inView.has(headSec)) head.near = [{ c: prog(headSec.getBoundingClientRect(), HH) * (head.n - 1), bias: 0 }];
        netPump();
      }, { rootMargin: '250% 0px' });
      approach.observe(headSec);
    }

    // The intro waits until its quarter turn is in memory (at most 2.5 s), then plays as approved
    const introReady = () => { for (let i = Math.round(-QUARTER); i <= 0; i++) if (!body.has(i)) return false; return true; };

    function frame(now) {
      if (!alive) return;
      const dt = Math.min(64, now - prev); prev = now; tick++;
      const rT = turn.getBoundingClientRect(), rH = headSec.getBoundingClientRect(), rV = viewSec.getBoundingClientRect();
      const centers = [];
      const near = [];

      if (firstAt && !start && !reduce && (introReady() || now - firstAt > 2500)) start = now;

      if (seen(rT)) {
        const rawP = prog(rT, SH);
        if (curP < 0) curP = rawP;
        curP += (rawP - curP) * ease(dt, TAU);
        if (Math.abs(rawP - curP) < .00004) curP = rawP;
        const p = curP;
        let intro = -QUARTER, rise = 9;
        if (reduce) { intro = 0; rise = 0; }
        else if (start) { const t = clamp((now - start) / INTRO, 0, 1); const e = 1 - Math.pow(1 - t, 4); intro = -QUARTER * (1 - e); rise = (1 - e) * 9; }
        const f = p * body.n + intro;
        const rb = Math.ceil(8 * R);
        body.focus(f, rb); body.focus(rawP * body.n + intro, rb, 2);
        centers.push(f, rawP * body.n + intro);
        near.push({ c: f, bias: 0 }, { c: rawP * body.n + intro, bias: 2 });
        drawSpin(f);

        const m = mobile();
        const x = m ? 0 : xAt(p);
        const sc = m ? 1 + .28 * smooth(clamp(p / .12, 0, 1)) : 1;
        put(spin, 'transform', 'translate3d(calc(-50% + ' + x.toFixed(2) + 'vw),' + rise.toFixed(2) + '%,0) scale(' + sc.toFixed(3) + ')');
        put(glow, 'transform', 'translate3d(' + x.toFixed(2) + 'vw,0,0)');

        const ho = Math.round(clamp(1 - p / .09, 0, 1) * 100) / 100;
        put(heroCopy, 'opacity', String(ho)); put(heroCopy, 'visibility', ho < .02 ? 'hidden' : 'visible');
        put(hint, 'opacity', String(ho));
        let active = -1, scrim = 0;
        for (let i = 0; i < caps.length; i++) {
          const c = caps[i], fade = .05;
          const o = Math.round(clamp(Math.min((p - c.a) / fade, (c.b - p) / fade), 0, 1) * 100) / 100;
          const mid = (c.a + c.b) / 2;
          put(c.el, 'opacity', String(o));
          // An invisible caption keeps its last position: nothing to move
          if (o > 0 || !m) put(c.el, 'transform', m ? 'translate3d(0,' + ((1 - o) * 14).toFixed(1) + 'px,0)' : 'translate3d(0,calc(-50% + ' + ((mid - p) * 320).toFixed(1) + 'px),0)');
          if (o > .5) active = i; if (o > scrim) scrim = o;
        }
        scrim = Math.round(scrim * 20) / 20;
        if (scrim !== lastScrim) { lastScrim = scrim; stage.style.setProperty('--scrim', scrim); }
        if (active !== lastActive) { lastActive = active; ticks.forEach((t, i) => t.classList.toggle('is-on', i === active)); }
      }

      const headOn = seen(rH);
      if (headOn) {
        const rawH = prog(rH, HH);
        if (curH < 0) curH = rawH;
        curH += (rawH - curH) * ease(dt, TAU);
        const hf = curH * (head.n - 1);
        const rh = Math.ceil(10 * R);
        head.focus(hf, rh); head.focus(rawH * (head.n - 1), rh, 2);
        head.near = [{ c: hf, bias: 0 }, { c: rawH * (head.n - 1), bias: 2 }];
        drawMacro(hf);
        const fi = curH < .26 ? 0 : curH < .72 ? 1 : 2;
        if (fi !== lastFeat) { lastFeat = fi; feats.forEach((f, i) => f.classList.toggle('is-on', i === Math.min(fi, feats.length - 1))); }
      } else if (rH.top < innerHeight * 2.5 && rH.bottom > -innerHeight * 2.5) {
        // Approaching the close-up: fetch from where it will start
        head.near = [{ c: prog(rH, HH) * (head.n - 1), bias: 0 }];
      }
      if (!head.on && rH.top < innerHeight * 2.5) head.on = true;

      if (seen(rV)) {
        if (!dragging && Math.abs(vel) > .01) { vel *= .95; setOrbit(of + vel); }
        body.focus(of, Math.ceil(8 * R)); centers.push(of);
        near.push({ c: of, bias: 0 });
        drawOrbit(of);
      }
      if (near.length) body.near = near;

      pump();
      netPump();
      if (tick % 20 === 0) {
        if (centers.length) body.evict(centers, Math.ceil(14 * R));
        head.evict(headOn ? [curH * (head.n - 1)] : [0], headOn ? Math.ceil(18 * R) : 2);
        measure();
      }
      if (viewKnown && !inView.size && !dragging && Math.abs(vel) <= .01) { running = false; return; }
      requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);

    /* drag viewer */
    const wrap = $('[data-cx-orbit]');
    const rot = $('[data-cx-rot]');
    const degrees = (d) => (rot.dataset.label || '[n]').replace('[n]', d);
    function setOrbit(f, fromSlider) { of = ((f % body.n) + body.n) % body.n; if (!fromSlider) { const d = Math.round(of / body.n * 360) % 360; rot.value = d; rot.setAttribute('aria-valuetext', degrees(d)); } }
    wrap.addEventListener('pointerdown', (e) => { dragging = true; lx = e.clientX; vel = 0; wrap.classList.add('is-drag'); wrap.setPointerCapture(e.pointerId); });
    wrap.addEventListener('pointermove', (e) => { if (!dragging) return; const dx = e.clientX - lx; lx = e.clientX; vel = -dx * .0025 * body.n; setOrbit(of + vel); });
    const end = () => { dragging = false; wrap.classList.remove('is-drag'); };
    wrap.addEventListener('pointerup', end); wrap.addEventListener('pointercancel', end);
    rot.addEventListener('input', () => { vel = 0; setOrbit(rot.value / 360 * body.n, true); rot.setAttribute('aria-valuetext', degrees(rot.value)); });
  }

  document.querySelectorAll('[data-cx]').forEach(boot);
  document.addEventListener('shopify:section:load', (e) => e.target.querySelectorAll('[data-cx]').forEach(boot));
})();
